/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import CollectionModule from 'client/store/modules/CollectionModule';
import PolicyTemplateApi from 'client/api/v2/PolicyTemplateApi';
import Vue from 'vue';
import configurationCategoriesConst from 'client/util/Constants/ConfigurationCategoriesConstants';

export class PolicyTemplateCollectionClass extends CollectionModule {
  constructor(os = '', ...args) {
    super(...args);
    this.os = os.toUpperCase();
  }

  extractModule() {
    const module = super.extractModule();

    module.getters.getUnfilteredCollection = (...params) => this
      .getUnfilteredCollection(...params);
    module.actions.filterByCategory = (...params) => this
      .filterByCategory(...params);
    module.mutations.SET_UNFILTERED_COLLECTION = (...params) => this
      .SET_UNFILTERED_COLLECTION(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = super.getInitialState();

    // Adding if statement for allowing a cross-platform collection if none is specified
    if (this.os) {
      state.features.items.filters = [
        {
          name: 'os_meta_family',
          operator: 'eq',
          value: this.os,
        },
      ];
    }

    state.features.items.pagination.limit = 100;

    state.entities.unfilteredItems = {
      nextPageToken: null,
      results: {},
      skipToken: null,
      totalCount: 0,
      totalCountOfAllItems: null,
    };

    return state;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  getUnfilteredCollection(state) {
    return state.entities.unfilteredItems.results;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Overriding the parent's function so that we can filter by categories.
   * The filtering is done entirely in the UI. See filterByCategory().
   * @param context
   * @param next
   * @returns {Promise<unknown>}
   */
  fetchCollection(context, { next = false } = {}) {
    const filters = context.getters.getFilters;
    const categoryFilter = filters && filters.find(obj => obj.name === 'category');

    return super
      .fetchCollection(context, { next })
      .then((ids) => {
        if (ids && categoryFilter) {
          const collection = context.getters.getCollection;
          const payload = {
            results: collection,
            totalCount: collection.length,
            nextPageToken: context.getters.getNextPageToken,
            skipToken: context.getters.getSkipToken,
          };
          context.commit('SET_UNFILTERED_COLLECTION', payload);
          context.dispatch('filterByCategory');
        }
      });
  }

  filterByCategory(context) {
    const filters = context.getters.getFilters;
    const categoryFilter = filters && filters.find(obj => obj.name === 'category');

    let collection = context.getters.getUnfilteredCollection;
    collection = collection.filter((policy) => {
      const categories = this.getCategoriesByPolicy(policy.name);
      return categories && categories.find(category => category === categoryFilter.value);
    });
    const payload = {
      results: collection,
      totalCount: collection.length,
      nextPageToken: context.getters.getNextPageToken,
      skipToken: context.getters.getSkipToken,
    };

    context.commit('RESET_COLLECTION', { soft: true });
    context.commit('SET_COLLECTION', payload);
    return Promise.resolve();
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  SET_FILTER(state, { filter, pagination }) {
    const { filters } = state.features.items;

    // If the filter already exists, update the value unless the operator is 'ne'.
    // there is currently a bug in the API with 'ne' and the | separator, so any
    // 'ne' filters need to be combined, not overwritten if one exists already.
    const existingFilterIndex = filters.findIndex(d => (
      d.name === filter.name && d.operator === filter.operator && d.operator !== 'ne'
    ));
    if (existingFilterIndex > -1) {
      Vue.set(state.features.items.filters, existingFilterIndex, filter);
      return;
    }

    // If the filter doesn't exist or it exists but it is a 'ne' operator, add the filter
    filters.push(filter);
    Vue.set(state.features.items, 'filters', filters);

    if (pagination) {
      const { limit } = pagination;
      state.features.items.pagination.limit = limit;
    }
  }

  SET_UNFILTERED_COLLECTION(state, items = {}) {
    Vue.set(state.entities, 'unfilteredItems', items);
  }

  // ----------------------------------------------------------------------------
  // Helper Methods
  // ----------------------------------------------------------------------------

  getCategoriesByPolicy(policyName) {
    let policyCategories;
    Object.values(configurationCategoriesConst).every((os) => {
      const policy = Object.values(os).find(p => p.name === policyName);
      if (policy) {
        policyCategories = policy.categories;
        return false;
      }
      return true;
    });
    return policyCategories;
  }
}

export function createPolicyTemplateCollectionClass(os) {
  return new PolicyTemplateCollectionClass(os, PolicyTemplateApi, 'id', '').extractModule();
}
